"use client";

import { useState, useSyncExternalStore } from "react";
import Link from "next/link";
import { ArrowRight, X } from "lucide-react";
import { LAUNCH_BANNERS } from "@/data/launch-banners";

const STORAGE_KEY = "ib:announcement:qubetics-solver:dismissed";

function subscribe(cb: () => void) {
  if (typeof window === "undefined") return () => {};
  const onStorage = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) cb();
  };
  window.addEventListener("storage", onStorage);
  return () => window.removeEventListener("storage", onStorage);
}

function getSnapshot() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

export default function AnnouncementBar() {
  const banner = LAUNCH_BANNERS.qubeticsSolver;
  const persistedDismissed = useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => false,
  );
  const [sessionDismissed, setSessionDismissed] = useState(false);

  if (!banner.enabled || persistedDismissed || sessionDismissed) return null;

  const dismiss = () => {
    setSessionDismissed(true);
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // No-op when storage is blocked.
    }
  };

  return (
    <div
      role="region"
      aria-label="Site announcement"
      className="border-b border-line bg-bg-surface"
      style={{
        backgroundImage:
          "linear-gradient(to right, transparent, color-mix(in oklab, var(--accent) 5%, transparent) 50%, transparent)",
      }}
    >
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-2.5 sm:gap-4 sm:px-6">
        <Link
          href={banner.href}
          className="group flex min-w-0 flex-1 items-center gap-3 text-[13px] leading-none sm:gap-4"
        >
          <span className="shrink-0 font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-brand-purple-light">
            {banner.eyebrow}
          </span>
          <span
            aria-hidden
            className="hidden h-3 w-px shrink-0 bg-white/12 sm:block"
          />
          <span className="hidden min-w-0 truncate text-text-secondary transition-colors duration-200 group-hover:text-white sm:inline">
            {banner.headline}
          </span>
          <span className="min-w-0 truncate text-text-secondary transition-colors duration-200 group-hover:text-white sm:hidden">
            {banner.headlineShort ?? banner.headline}
          </span>
          <span className="ml-auto hidden shrink-0 items-center gap-1.5 whitespace-nowrap text-brand-purple-light sm:inline-flex">
            <span className="font-medium">Read setup guide</span>
            <ArrowRight
              size={13}
              strokeWidth={2}
              className="transition-transform duration-200 group-hover:translate-x-0.5"
              aria-hidden
            />
          </span>
          <ArrowRight
            size={13}
            strokeWidth={2}
            className="ml-auto shrink-0 text-brand-purple-light transition-transform duration-200 group-hover:translate-x-0.5 sm:hidden"
            aria-hidden
          />
        </Link>
        <span aria-hidden className="h-3 w-px shrink-0 bg-white/10" />
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss announcement"
          className="-mr-1 shrink-0 p-1 text-text-muted transition-colors duration-200 hover:text-text-primary"
        >
          <X size={14} strokeWidth={2.25} aria-hidden />
        </button>
      </div>
    </div>
  );
}
